/**
 * 교재 학습활동의 data-id 를 사람이 읽는 위치 경로(breadcrumb)로 디코딩하는 공용 유틸.
 *
 * 학습활동 결과 화면(목록/상세)에서 활동의 `data-id`(예: `beg1-l1-pep-ex3`)를
 * 교재(barcode)별 맵핑 테이블로 디코딩해 `Lesson 1 › Patterns... › Exercise 3` 형태로 표시한다.
 *
 * 관련 문서: docs/API_TEXTBOOK_DATAID_MAPPING.md, docs/PORTING_DATAID_LOCATION_NAV.md
 */

/**
 * data-id 를 교재 맵핑으로 디코딩해 위치명 배열(breadcrumb)을 반환.
 *
 * - `key` 는 data-id 의 하이픈 경계 구간(한 토큰 `pep` 또는 여러 토큰 묶음 `beg1-l1`).
 * - 매칭: `-${dataId}-` 안에 `-${key}-` 가 포함되면 매칭(하이픈 경계로 lis3↔lis30 오매칭 방지).
 * - 정렬: depth → data-id 내 위치. 같은 value 는 중복 제거.
 *
 * 단순 `split("-")` 가 아닌 이유: `key` 가 `beg1-l1` 처럼 `-` 를 포함할 수 있어
 * 토큰 단위 분리로는 매칭되지 않는다. 그래서 "하이픈 경계 구간 포함" 방식을 쓴다.
 *
 * @param {string} dataId 활동의 data-id (예: `beg1-l1-pep-ex3`)
 * @param {Array<{depth:number, key:string, value:string}>} mappings 교재별 맵핑 목록
 * @returns {string[]} 위치명 breadcrumb. 매칭 없으면 [].
 */
export function decodeDataIdLocation(dataId, mappings) {
  if (!dataId || !Array.isArray(mappings) || mappings.length === 0) return [];

  const haystack = `-${dataId}-`;
  const token = (key) => `-${String(key).trim()}-`;

  const matched = mappings
    .filter((m) => m && m.key && haystack.includes(token(m.key)))
    .sort(
      (a, b) =>
        (a.depth - b.depth) ||
        (haystack.indexOf(token(a.key)) - haystack.indexOf(token(b.key)))
    );

  const seen = new Set();
  const crumbs = [];
  matched.forEach((m) => {
    if (m.value && !seen.has(m.value)) {
      seen.add(m.value);
      crumbs.push(m.value);
    }
  });
  return crumbs;
}

export default { decodeDataIdLocation };
